"use client";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Box, Button, Flex, Heading, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { $getRoot } from 'lexical';
import { useCallback, useMemo, useState } from 'react';
import { VariableNode } from './nodes/variable-node';
import { InitialStatePlugin } from './plugins/initial-state-plugin';
import { VariableDropdownPlugin } from './plugins/variable-dropdown';
import { VariableTypeaheadPlugin } from './plugins/variable-typeahead-plugin';
import { Undo } from 'iconoir-react';
import { TemplateChannel, templateContentMaxLength, templateContentWarnLength, } from '@fluenceur/types';
const MAX_CHARACTERS = templateContentMaxLength(TemplateChannel.INSTAGRAM_DM);
const WARNING_THRESHOLD = templateContentWarnLength(TemplateChannel.INSTAGRAM_DM);
const DEFAULT_LABELS = {
    title: 'Message',
    reset: 'Reset',
    placeholder: 'Write your invitation message...',
    insertVariable: 'Insert variable',
};
function CharacterCountPlugin({ characterCount, maxCharacters, warningThreshold, }) {
    const isWarning = characterCount >= warningThreshold;
    const isError = characterCount > maxCharacters;
    return (_jsxs(Text, { fontSize: "sm", color: isError ? 'red.600' : isWarning ? 'orange.600' : 'gray.600', whiteSpace: "nowrap", children: [characterCount, " / ", maxCharacters] }));
}
export function InvitationMessageEditor({ initialContent, availableVariables, onContentChange, onReset, labels: labelsProp, translateFn, footerTrailing, }) {
    const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelsProp }), [labelsProp]);
    const [characterCount, setCharacterCount] = useState(0);
    const [editorKey, setEditorKey] = useState(0);
    const editorConfig = useMemo(() => ({
        namespace: 'InvitationMessageEditor',
        nodes: [VariableNode],
        theme: {
            paragraph: 'editor-paragraph',
            text: {
                bold: 'editor-text-bold',
                italic: 'editor-text-italic',
            },
        },
        onError: (_error) => {
            console.error('Lexical error:', _error);
        },
    }), []);
    const handleEditorChange = useCallback((editorState) => {
        editorState.read(() => {
            const root = $getRoot();
            const plainText = root.getTextContent();
            const charCount = plainText.length;
            setCharacterCount(charCount);
            onContentChange(editorState, plainText);
        });
    }, [onContentChange]);
    const handleReset = useCallback(() => {
        setEditorKey((prev) => prev + 1);
        onReset();
    }, [onReset]);
    return (_jsx(Box, { rounded: "xl", p: 4, bg: "gray.50", children: _jsxs(VStack, { align: "stretch", gap: 3, children: [_jsxs(Flex, { justify: "space-between", align: "center", flexWrap: "wrap", gap: 2, children: [_jsx(Heading, { as: "h3", fontSize: "md", children: labels.title }), _jsx(HStack, { children: _jsx(Button, { variant: "ghost", size: "xs", fontSize: "sm", fontWeight: "medium", onClick: handleReset, children: _jsxs(HStack, { gap: 1, children: [_jsx(Undo, { width: 16, height: 16 }), _jsx("span", { children: labels.reset })] }) }) })] }), _jsx(LexicalComposer, { initialConfig: editorConfig, children: _jsxs(Stack, { gap: 2, children: [_jsx(Box, { position: "relative", children: _jsxs(Box, { className: "editor-container", border: "1px solid", borderColor: "gray.100", rounded: "xl", bg: "white", minH: "150px", p: 3, transition: "box-shadow 0.2s ease-in-out", _hover: {
                                        shadow: 'sm',
                                        transition: 'box-shadow 0.2s ease-in-out',
                                    }, children: [_jsx(PlainTextPlugin, { contentEditable: _jsx(ContentEditable, { className: "editor-input", style: {
                                                    minHeight: '120px',
                                                    outline: 'none',
                                                    fontSize: '14px',
                                                    lineHeight: '1.6',
                                                }, "aria-label": labels.title }), placeholder: _jsx(Box, { position: "absolute", top: "12px", left: "12px", color: "gray.400", fontSize: "14px", pointerEvents: "none", userSelect: "none", children: labels.placeholder }), ErrorBoundary: LexicalErrorBoundary }), _jsx(OnChangePlugin, { onChange: handleEditorChange }), _jsx(HistoryPlugin, {}), _jsx(VariableTypeaheadPlugin, { availableVariables: availableVariables, translateFn: translateFn }), _jsx(InitialStatePlugin, { initialContent: initialContent })] }) }), _jsxs(HStack, { justify: "space-between", align: "center", children: [_jsx(VariableDropdownPlugin, { availableVariables: availableVariables, translateFn: translateFn, insertVariableLabel: labels.insertVariable }), footerTrailing, _jsx(CharacterCountPlugin, { characterCount: characterCount, maxCharacters: MAX_CHARACTERS, warningThreshold: WARNING_THRESHOLD })] })] }) }, editorKey)] }) }));
}
